import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { addDays, format, parseISO } from 'date-fns'
import { Button } from '@cannect/new-components/atoms'
import { usePostponeSubscription } from '@cannect/services/resources/recurrency/hooks'
import { formatApiDate } from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionDetail } from '@cannect/services/resources/recurrency/types'
import { subscriptionsCopy } from '../copy'
import { useSubscriptionAction } from '../hooks/useSubscriptionAction'
import { DateField } from './DateField'
import { DialogShell } from './DialogShell'

const copy = subscriptionsCopy.postpone

type Props = { detail: SubscriptionDetail; open: boolean; onOpenChange: (open: boolean) => void }

// Limites do servidor: depois da data prevista do ciclo e até `maxPostponeAt`.
export const postponeRange = (detail: SubscriptionDetail) => {
  const base = detail.currentCycle?.scheduledAt || detail.nextChargeAt
  const min = base ? format(addDays(parseISO(base.slice(0, 10)), 1), 'yyyy-MM-dd') : ''
  return { min, max: detail.maxPostponeAt || '' }
}

export const PostponeDialog = ({ detail, open, onOpenChange }: Props) => {
  const { min, max } = postponeRange(detail)
  const rangeMessage = copy.range(formatApiDate(min), formatApiDate(max))
  const schema = z.object({
    date: z
      .string()
      .min(1, copy.required)
      .refine((value) => (!min || value >= min) && (!max || value <= max), rangeMessage)
  })
  const form = useForm<{ date: string }>({ resolver: zodResolver(schema), defaultValues: { date: '' } })

  useEffect(() => {
    if (!open) form.reset({ date: '' })
  }, [open])

  const action = useSubscriptionAction({
    detail,
    mutation: usePostponeSubscription(detail.id),
    event: 'recurrency_postpone',
    fieldErrorCodes: ['POSTPONE_LIMIT', 'POSTPONE_ALREADY_USED'],
    onFieldError: (err) => form.setError('date', { message: err.message })
  })

  const onSubmit = form.handleSubmit(async ({ date }) => {
    if (await action.run({ nextChargeAt: date })) onOpenChange(false)
  })

  return (
    <DialogShell open={open} onOpenChange={onOpenChange} title={copy.title} description={copy.once}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <DateField
          label={copy.field}
          min={min || undefined}
          max={max || undefined}
          hint={max ? rangeMessage : undefined}
          error={form.formState.errors.date?.message}
          {...form.register('date')}
        />
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline_light" onClick={() => onOpenChange(false)}>
            {subscriptionsCopy.buttons.back}
          </Button>
          <Button type="submit" isLoading={action.isPending}>
            {copy.confirm}
          </Button>
        </div>
      </form>
    </DialogShell>
  )
}
